// presenceStore.js — the SINGLE source of truth for the staff presence status snapshot (WAVE 2a-3).
//
// Joey (Nov 3): the phone dock shows the SAME 4 presence states, the SAME words as the top-bar selector,
// read and written through the ONE presence record — no local-only status anywhere. (A future live-calling
// wave adds a 5th "On a call" state set automatically during a call.)
//
// The presence RECORD lives on the backend (/dispositions/presence). This module is the in-memory MIRROR of
// the caller's current status, so the top-bar PresenceSelector and the phone-dock pill — rendered in
// different parts of the tree — always reflect the same value the instant any control changes it. React 19's
// useSyncExternalStore keeps every subscriber in sync.
import { useSyncExternalStore } from 'react';
import api from '../utils/api';
import { PRESENCE_DEFAULT } from '../constants/presence';

let snapshot = { status: PRESENCE_DEFAULT, meetingLabel: '' };
const listeners = new Set();
let fetched = false;

function emit() {
  listeners.forEach((l) => { try { l(); } catch { /* ignore */ } });
}

// Update the mirror after a read or a write. Both controls call this (via commit) so they never diverge.
export function setPresenceSnapshot(status, meetingLabel) {
  const next = { status: status || PRESENCE_DEFAULT, meetingLabel: meetingLabel || '' };
  if (next.status === snapshot.status && next.meetingLabel === snapshot.meetingLabel) return;
  snapshot = next;
  emit();
}

// Read the live status from the backend once (idempotent). Any control may call this on mount; the first
// call fetches, later calls are no-ops unless force=true. Degrades silently (keeps the Ready default).
export function fetchPresenceOnce(force = false) {
  if (fetched && !force) return;
  fetched = true;
  api.get('/dispositions/presence/current')
    .then((res) => {
      const p = res?.data?.presence;
      setPresenceSnapshot(p?.status, p?.meeting_type_label);
    })
    .catch(() => { /* leave default */ });
}

// Clear the mirror on a user switch (logout/login). The store is a module-level singleton, and this app's
// logout/login are SPA-only (no page reload), so without this a second employee signing in on the same tab
// would briefly see the previous user's status/meeting label until their first write. Call from auth.
export function resetPresenceStore() {
  fetched = false;
  snapshot = { status: PRESENCE_DEFAULT, meetingLabel: '' };
  emit();
}

function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot() {
  return snapshot;
}

export function usePresenceSnapshot() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
